// job-web-main/frontend/src/components/dashboard/MatchesTab.js
import { useEffect, useState } from 'react';
import { ArrowLeft, CheckCircle2, AlertTriangle, MapPin } from 'lucide-react';
import { getDashboardMatches, applyToMatch, portalErrorMessage } from '../../api/portal';
import { salaryText, salaryKesText } from '../../lib/salary';
import './dashboardTabs.css';

function tierLabel(m) {
  if (m.tier === 'great') return 'Great match';
  if (m.tier === 'good') return 'Good match';
  if (m.tier === 'pending') return 'Applied';
  return 'Profession match';
}

// onVisitOffice (D20, 2026-07-25): Matches never renders a booking widget itself — a weak match's
// "Go to Status to book a visit" action switches the parent's tab instead. The one, single booking
// widget lives entirely in StatusTab, reused across every stage that needs a visit.
export default function MatchesTab({ onVisitOffice }) {
  const [matches, setMatches] = useState(null);
  const [error, setError] = useState(null);
  const [applyingId, setApplyingId] = useState(null);
  const [view, setView] = useState('list');
  const [activeId, setActiveId] = useState(null);

  useEffect(() => {
    let live = true;
    getDashboardMatches().then((m) => { if (live) setMatches(m); }).catch((e) => { if (live) setError(portalErrorMessage(e, 'Could not load your matches.')); });
    return () => { live = false; };
  }, []);

  async function handleApply(demandId) {
    setApplyingId(demandId);
    try {
      await applyToMatch(demandId);
      setMatches((prev) => prev.map((m) => (m.id === demandId ? { ...m, applied: true } : m)));
    } catch (e) {
      setError(portalErrorMessage(e, 'Could not apply — please try again.'));
    } finally {
      setApplyingId(null);
    }
  }

  if (error) return <div className="apply-error" role="alert">{error}</div>;
  if (!matches) return null;
  if (matches.length === 0) {
    return <div className="dash-empty"><p>No matches yet — check back soon.</p></div>;
  }

  if (view === 'detail') {
    const m = matches.find((x) => x.id === activeId) || matches[0];
    const anySelected = matches.some((x) => x.selected);
    const lockedOut = anySelected && !m.selected;
    return (
      <div className="dash-panel">
        <button type="button" className="dtab-detail-back" onClick={() => setView('list')}>
          <ArrowLeft size={16} /> Back to matches
        </button>
        <div className="dtab-match-toprow">
          <span className="dtab-tier">
            <span className={`dtab-tier-dot ${m.tier}`} />
            {tierLabel(m)} {m.weak ? <span className="dtab-tier-pct dtab-cv-zero">{m.pct}% CV match</span> : <span className="dtab-tier-pct">· {m.pct}%</span>}
          </span>
          {m.selected ? (
            <span className="dtab-match-selected">Selected</span>
          ) : m.applied ? (
            <span className="dtab-match-applied">Applied</span>
          ) : null}
        </div>
        <h3 className="dtab-match-title">{m.title}</h3>
        <p className="dtab-match-ref">Ref: {m.jobRef || '—'}</p>
        <ul className="dtab-checklist">
          <li><CheckCircle2 size={15} /> Matches your profession{m.profession ? `: ${m.profession}` : ''}</li>
          {m.weak ? (
            <li className="dtab-cv-zero"><AlertTriangle size={15} /> <b>CV match: {m.pct}%</b> — your CV doesn&apos;t show experience matching this role yet</li>
          ) : (
            <li><CheckCircle2 size={15} /> Matched per your CV</li>
          )}
        </ul>
        <div className="dtab-fact-row">
          <span className={m.accommodation ? 'jd-bchip jd-bchip-provided' : 'jd-bchip'}>Accommodation</span>
          <span className={m.transport ? 'jd-bchip jd-bchip-provided' : 'jd-bchip'}>Transport</span>
          <span className={m.food ? 'jd-bchip jd-bchip-provided' : 'jd-bchip'}>Food</span>
        </div>
        <p className="dtab-match-salary" style={{ marginBottom: 14 }}>
          {salaryText(m.salary)} {salaryKesText(m.salary) && <span style={{ fontWeight: 600, color: 'var(--text-grey)' }}>{salaryKesText(m.salary)}</span>}
        </p>
        {m.jd && <p className="dtab-jd-text">{m.jd}</p>}
        {m.weak && (
          <div className="dtab-assist-block">
            <p className="dtab-assist-title"><MapPin size={14} /> We can help in person</p>
            <p className="dtab-assist-body">Visit our office and a member of staff will go through your CV with you.</p>
            <button type="button" className="dtab-assist-btn" onClick={onVisitOffice}>
              Go to Status to book a visit
            </button>
          </div>
        )}
        <div className="dtab-detail-apply">
          {m.selected ? (
            <button type="button" className="apply-btn" disabled style={{ opacity: .6 }}>You were selected for this role</button>
          ) : lockedOut ? (
            <button type="button" className="apply-btn" disabled style={{ opacity: .6 }}>Not available — you&apos;re already placed</button>
          ) : m.applied ? (
            <button type="button" className="apply-btn" disabled style={{ opacity: .6 }}>Applied</button>
          ) : (
            <button type="button" className="apply-btn" disabled={applyingId === m.id} onClick={() => handleApply(m.id)}>
              {applyingId === m.id ? 'Applying…' : (m.weak ? 'Apply anyway' : 'Apply Now')}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="dash-panel">
      {matches.map((m) => (
        <button key={m.id} type="button" className={m.dimmed ? 'dtab-match-card is-dimmed' : 'dtab-match-card'}
          onClick={() => { setActiveId(m.id); setView('detail'); }}>
          <div className="dtab-match-toprow">
            <span className="dtab-tier">
              <span className={`dtab-tier-dot ${m.tier}`} />
              {tierLabel(m)} {m.weak ? <span className="dtab-tier-pct dtab-cv-zero">{m.pct}% CV match</span> : <span className="dtab-tier-pct">· {m.pct}%</span>}
            </span>
            {m.selected ? (
              <span className="dtab-match-selected">Selected</span>
            ) : m.applied ? (
              <span className="dtab-match-applied">Applied</span>
            ) : null}
          </div>
          <h3 className="dtab-match-title">{m.title}</h3>
          <p className="dtab-match-ref">Ref: {m.jobRef || '—'}</p>
          <div className="dtab-fact-row">
            <span className={m.accommodation ? 'jd-bchip jd-bchip-provided' : 'jd-bchip'}>Accommodation</span>
            <span className={m.transport ? 'jd-bchip jd-bchip-provided' : 'jd-bchip'}>Transport</span>
            <span className={m.food ? 'jd-bchip jd-bchip-provided' : 'jd-bchip'}>Food</span>
          </div>
          <div className="dtab-match-bottomrow">
            <div>
              <div className="dtab-match-salary">{salaryText(m.salary)}</div>
              {salaryKesText(m.salary) && <div style={{ fontSize: 11, color: 'var(--text-grey)' }}>{salaryKesText(m.salary)}</div>}
            </div>
          </div>
          {m.weak && (
            <div className="dtab-weak-banner">
              <AlertTriangle size={15} />
              <span className="dtab-weak-banner-text">Not a strong match to your CV yet. <b>Visit our office</b> and we&apos;ll help you in person.</span>
            </div>
          )}
        </button>
      ))}
    </div>
  );
}
